import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import {
  getWeeklyGeneralSupplierOrderCounts,
  getMonthlyGeneralSupplierOrderCounts,
} from "../../slices/thunks";
import { createSelector } from "reselect";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import ReactApexChart from "react-apexcharts";
import {
  DASHBOARD_DATE_FILTER_PERIODS,
  getDashboardDateRange,
  shouldUseMonthlyEndpoint,
} from "./dateFilterUtils";
import { toChartItems, toChartLabel, toChartNumber } from "./dashboardChartUtils";

const formatCurrency = (value) =>
  toChartNumber(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const WeeklyTotalOrderAmount = ({ dateFilter = null }) => {
  const dispatch = useDispatch();
  const dateFrom = useRef(null);
  const dateTo = useRef(null);
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const isGlobalDateFilter = Boolean(dateFilter);
  const selectDashboardData = createSelector(
    (state) => state.Dashboard,
    (dashboard) => dashboard
  );
  // Inside your component
  const { weeklyGeneralSupplierOrderCounts } = useSelector(selectDashboardData);

  const [series, setSeries] = useState([
    {
      name: "Ordered",
      type: "column",
      data: [],
      color: "#0AB39C",
    },
    {
      name: "Cancelled",
      type: "column",
      data: [],
      color: "#F06548",
    },
    {
      name: "Total Price",
      type: "line",
      data: [],
      color: "#00E396",
    },
  ]);
  const [options, setOptions] = useState({
    chart: {
      height: 350,
      type: "line",
      stacked: false,
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true | '<img src="/static/icons/reset.png" width="20">',
          customIcons: [],
        },
      },
    },
    stroke: {
      width: [0, 0, 3],
      curve: "smooth",
    },
    plotOptions: {
      bar: {
        columnWidth: "45%",
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories: [],
      tooltip: {
        enabled: true,
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: function (value, { seriesIndex }) {
          if (seriesIndex === 2) {
            return formatCurrency(value);
          }

          return value;
        },
      },
    },
    yaxis: [
      {
        seriesName: "Ordered",
        forceNiceScale: true,
        decimalsInFloat: 0,
        title: {
          text: "Counts",
        },
        labels: {
          formatter: function (value) {
            return Math.round(toChartNumber(value)).toString();
          },
        },
      },
      {
        seriesName: "Cancelled",
        show: false,
      },
      {
        seriesName: "Total Price",
        opposite: true,
        title: {
          text: "Total Price",
        },
        labels: {
          formatter: function (value) {
            return formatCurrency(value);
          },
        },
      },
    ],
  });

  const getData = () => {
    const data = {
      dateFrom:
        dateFrom.current != null
          ? moment(dateFrom.current).format("YYYY-MM-DD")
          : null,
      dateTo:
        dateTo.current != null
          ? moment(dateTo.current).format("YYYY-MM-DD")
          : null,
    };
    if (isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)) {
      dispatch(getMonthlyGeneralSupplierOrderCounts(data));
    } else {
      dispatch(getWeeklyGeneralSupplierOrderCounts(data));
    }
  };

  const periodLabel =
    isGlobalDateFilter && shouldUseMonthlyEndpoint(dateFilter)
      ? "Monthly"
      : "Weekly";

  useEffect(() => {
    if (weeklyGeneralSupplierOrderCounts) {
      const chartItems = toChartItems(weeklyGeneralSupplierOrderCounts);

      // Update series and options based on the fetched data
      setSeries([
        {
          name: "Ordered",
          type: "column",
          data: chartItems.map((item) => toChartNumber(item?.orderedCount)),
          color: "#0AB39C",
        },
        {
          name: "Cancelled",
          type: "column",
          data: chartItems.map((item) => toChartNumber(item?.cancelledCount)),
          color: "#F06548",
        },
        {
          name: "Total Price",
          type: "line",
          data: chartItems.map((item) => toChartNumber(item?.totalPrice)),
          color: "#00E396",
        },
      ]);

      setOptions((prevOptions) => ({
        ...prevOptions,
        xaxis: {
          ...prevOptions.xaxis,
          categories: chartItems.map((item) => toChartLabel(item?.label)),
        },
      }));
    }
  }, [weeklyGeneralSupplierOrderCounts]);

  useEffect(() => {
    if (!isGlobalDateFilter) {
      return;
    }

    const range = getDashboardDateRange(
      dateFilter.period,
      dateFilter.customDateFrom,
      dateFilter.customDateTo
    );

    dateFrom.current = range.dateFrom;
    dateTo.current = range.dateTo;

    if (
      dateFilter.period !== DASHBOARD_DATE_FILTER_PERIODS.CUSTOM ||
      range.dateFrom ||
      range.dateTo
    ) {
      getData();
    }
  }, [
    dateFilter?.customDateFrom,
    dateFilter?.customDateTo,
    dateFilter?.period,
    isGlobalDateFilter,
  ]);

  useEffect(() => {
    if (isGlobalDateFilter) {
      return;
    }

    switch (selectedPeriod) {
      case "7 days":
        const date = new Date();
        const sevenDaysAgo = new Date(date);
        sevenDaysAgo.setDate(date.getDate() - 7);
        dateFrom.current = sevenDaysAgo;
        dateTo.current = new Date();
        break;
      case "30 days":
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(new Date().getDate() - 30);
        dateFrom.current = thirtyDaysAgo;
        dateTo.current = new Date();
        break;
      case "all":
        dateFrom.current = null;
        dateTo.current = null;
        break;
      case "custom":
        break;
      default:
        dateFrom.current = null;
        dateTo.current = null;
        break;
    }
    if (selectedPeriod !== "custom") {
      getData();
    }
  }, [isGlobalDateFilter, selectedPeriod]);

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            {periodLabel} Total Order Amount
          </h4>
          {!isGlobalDateFilter && <div className="d-flex gap-1">
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "all" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("all");
              }}
            >
              ALL
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "7 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("7 days");
              }}
            >
              7 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "30 days" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("30 days");
              }}
            >
              30 Days
            </button>
            <button
              type="button"
              className={`btn btn-soft-${
                selectedPeriod === "custom" ? "success" : "secondary"
              } btn-sm shadow-none`}
              onClick={() => {
                setSelectedPeriod("custom");
              }}
            >
              Custom
            </button>
            {selectedPeriod === "custom" && (
              <div className="d-flex align-items-center gap-2 mt-2">
                <Flatpickr
                  className="form-control"
                  id="datepicker-publish-input"
                  placeholder="Select a date from"
                  onChange={(selectedDates, dateStr, instance) => {
                    dateFrom.current = selectedDates[0];
                    getData();
                  }}
                  options={{
                    maxDate: dateTo.current,
                    mode: "single",
                    dateFormat: "m-d-Y",
                  }}
                />
                <span>to</span>
                <Flatpickr
                  className="form-control"
                  id="datepicker-publish-input"
                  placeholder="Select a date to"
                  onChange={(selectedDates, dateStr, instance) => {
                    dateTo.current = selectedDates[0];
                    getData();
                  }}
                  options={{
                    mode: "single",
                    minDate: dateFrom.current,
                    dateFormat: "m-d-Y",
                  }}
                />
              </div>
            )}
          </div>}
        </CardHeader>
        {/*}
        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp start={0} end={7585} duration={3} separator="," />
                </h5>
                <p className="text-muted mb-0">Orders</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp
                    suffix="k"
                    prefix="$"
                    start={0}
                    decimals={2}
                    end={22.89}
                    duration={3}
                  />
                </h5>
                <p className="text-muted mb-0">Earnings</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1">
                  <CountUp start={0} end={367} duration={3} />
                </h5>
                <p className="text-muted mb-0">Refunds</p>
              </div>
            </Col>
            <Col xs={6} sm={3}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1 text-success">
                  <CountUp
                    start={0}
                    end={18.92}
                    decimals={2}
                    duration={3}
                    suffix="%"
                  />
                </h5>
                <p className="text-muted mb-0">Conversation Ratio</p>
              </div>
            </Col>
          </Row>
        </CardHeader>
*/}
        <CardBody className="p-0 pb-2">
          <div className="w-100">
            <div dir="ltr">
              {weeklyGeneralSupplierOrderCounts ? (
                <ReactApexChart
                  options={options}
                  series={series}
                  type="line"
                  height={350}
                />
              ) : (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default WeeklyTotalOrderAmount;
